import { useCallback, useEffect, useMemo, useState } from 'react';
import { useT } from '../i18n';
import type { MailClient } from '../api/client';
import type { Mailbox } from '../api/types';
import EmptyFolderDialog from './EmptyFolderDialog';
import {
    buildTree, displayLabel, flatten, isDescendant, resolveSpecial, roleFromName, type FolderNode, type Role,
} from '../mail/folders';

/*
    The folder sidebar: a real tree, and the only place folders can be managed.

    Every mutation goes straight to the server and then asks the parent to
    reload, rather than editing a local copy. Folder state is small and shared
    with rules, the move picker and the composer, so one source of truth is
    cheaper than reconciling four.
*/

const ICONS: Record<Role | 'folder', string> = {
    inbox: 'M3 12h4l2 3h6l2-3h4M3 12l2-7h14l2 7v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-6z',
    drafts: 'M4 20h16M6 16.5V13l8.5-8.5a2 2 0 0 1 3 3L9 16H6z',
    sent: 'M21 3L10.5 13.5M21 3l-6.5 18-4-8-8-4L21 3z',
    archive: 'M3.5 6.5h17v3.5h-17zM5.5 10v9a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1v-9M10 14h4',
    junk: 'M12 3l9 16H3l9-16zM12 9v5M12 16.5v.01',
    trash: 'M4 7h16M9.5 7V4.8h5V7M6.5 7l.9 13h9.2l.9-13',
    folder: 'M3 7.5a2 2 0 0 1 2-2h3.6l2 2.2H19a2 2 0 0 1 2 2v8.8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-11z',
};

const COLLAPSE_KEY = 'webmail.folders.collapsed.v1';
const HIDDEN_KEY = 'webmail.folders.showHidden.v1';

function loadCollapsed(accountId: string): Set<string> {
    try {
        const all = JSON.parse(localStorage.getItem(COLLAPSE_KEY) ?? '{}') as Record<string, string[]>;
        return new Set(all[accountId] ?? []);
    } catch {
        return new Set();
    }
}

function saveCollapsed(accountId: string, ids: Set<string>) {
    try {
        const all = JSON.parse(localStorage.getItem(COLLAPSE_KEY) ?? '{}') as Record<string, string[]>;
        all[accountId] = [...ids];
        localStorage.setItem(COLLAPSE_KEY, JSON.stringify(all));
    } catch {
        /* private mode — the tree still works, it just won't remember */
    }
}

type Menu = { node: FolderNode; x: number; y: number };
type Prompting =
    | { kind: 'create'; parent: Mailbox | null; value: string }
    | { kind: 'rename'; mailbox: Mailbox; value: string }
    | { kind: 'delete'; mailbox: Mailbox; value: string };

export default function FolderTree({
    client,
    accountId,
    accountKey,
    mailboxes,
    selectedId,
    onSelect,
    onChanged,
    onMoveMessage,
}: {
    client: MailClient | undefined;
    accountId: string | undefined;
    /** Local account id — collapse state is remembered per signed-in mailbox. */
    accountKey: string;
    mailboxes: Mailbox[];
    selectedId: string | undefined;
    onSelect: (id: string) => void;
    onChanged: () => void;
    /** A message dragged from the list onto a folder. */
    onMoveMessage?: (emailId: string, mailboxId: string) => void;
}) {
    const [collapsed, setCollapsed] = useState<Set<string>>(() => loadCollapsed(accountKey));
    const [showHidden, setShowHidden] = useState(() => localStorage.getItem(HIDDEN_KEY) === '1');
    const [menu, setMenu] = useState<Menu | null>(null);
    const [prompting, setPrompting] = useState<Prompting | null>(null);
    const [emptying, setEmptying] = useState<Mailbox | null>(null);
    const [dragId, setDragId] = useState<string | null>(null);
    const [dropId, setDropId] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [repaired, setRepaired] = useState(false);
    const { t, num } = useT();
    // Special folders read in the user's language; folders they named stay as named.
    const label = useCallback((m: Mailbox) => displayLabel(m, (role) => t(`folders.role.${role}` as const)), [t]);

    useEffect(() => {
        setCollapsed(loadCollapsed(accountKey));
        setRepaired(false);
    }, [accountKey]);

    const { byRole, unstamped } = useMemo(() => resolveSpecial(mailboxes), [mailboxes]);

    /*
        The inbox is never hidden. A server need not mark it subscribed — the development one does
        not — and with the unsubscribed ones tucked away the inbox vanished from the tree the moment
        another folder was opened (seen 2026-10-12, Gelen Kutusu missing beside the Trash).
    */
    const inboxId = byRole.inbox?.id;
    const hidden = useMemo(() => mailboxes.filter((m) => !m.isSubscribed && m.id !== inboxId), [mailboxes, inboxId]);
    const visible = useMemo(
        () => (showHidden ? mailboxes : mailboxes.filter((m) => m.isSubscribed || m.id === selectedId || m.id === inboxId)),
        [mailboxes, showHidden, selectedId, inboxId],
    );

    const rows = useMemo(() => flatten(buildTree(visible), collapsed), [visible, collapsed]);

    const toggle = useCallback(
        (id: string) => {
            setCollapsed((prev) => {
                const next = new Set(prev);
                if (next.has(id)) next.delete(id);
                else next.add(id);
                saveCollapsed(accountKey, next);
                return next;
            });
        },
        [accountKey],
    );

    const run = useCallback(
        async (fn: () => Promise<unknown>) => {
            setBusy(true);
            setError(null);
            try {
                await fn();
                onChanged();
            } catch (e) {
                setError(e instanceof Error ? e.message : String(e));
            } finally {
                setBusy(false);
            }
        },
        [onChanged],
    );

    /*
        Writes the role the name already implies onto the server. That is the
        difference between this app knowing where Archive is and every client
        knowing — including Thunderbird, which is the fallback door.
    */
    const stampRoles = () =>
        run(async () => {
            if (!client || !accountId) return;
            for (const { role, mailbox } of unstamped) {
                await client.updateMailbox(accountId, mailbox.id, { role });
            }
            setRepaired(true);
        });

    const reparent = (id: string, parentId: string | null) =>
        run(async () => {
            if (!client || !accountId) return;
            await client.updateMailbox(accountId, id, { parentId });
        });

    const submitPrompt = () => {
        if (!prompting || !client || !accountId) return;
        const value = prompting.value.trim();

        if (prompting.kind === 'create') {
            if (!value) return;
            const parent = prompting.parent;
            void run(async () => {
                await client.createMailbox(accountId, { name: value, parentId: parent?.id ?? null });
                if (parent) {
                    setCollapsed((prev) => {
                        const next = new Set(prev);
                        next.delete(parent.id);
                        saveCollapsed(accountKey, next);
                        return next;
                    });
                }
            });
        } else if (prompting.kind === 'rename') {
            if (!value || value === prompting.mailbox.name) return setPrompting(null);
            void run(() => client.updateMailbox(accountId, prompting.mailbox.id, { name: value }));
        } else {
            // Deleting a folder that holds mail takes the mail with it, so the
            // name has to be typed out. Nothing here is recoverable afterwards.
            const holdsMail = prompting.mailbox.totalEmails > 0;
            if (holdsMail && value !== prompting.mailbox.name) return;
            void run(() => client.destroyMailbox(accountId, prompting.mailbox.id, holdsMail));
        }
        setPrompting(null);
    };

    const canManage = Boolean(client && accountId);

    // The special role of the folder the menu is open on, read the way the tree reads it.
    const menuRole = useMemo(() => {
        if (!menu) return null;
        const m = menu.node.mailbox;
        const role = (m.role as Role | null) ?? roleFromName(m.name);
        return role && byRole[role]?.id === m.id ? role : null;
    }, [menu, byRole]);

    return (
        <div style={{ flex: 1, overflowY: 'auto', padding: '4px 8px 8px' }} onClick={() => setMenu(null)}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 6px 8px' }}>
                <span className="kicker">// {t('folders.title')}</span>
                <div style={{ flex: 1 }} />
                <button
                    data-hit
                    onClick={() => setPrompting({ kind: 'create', parent: null, value: '' })}
                    disabled={!canManage || busy}
                    title={t('folders.new')}
                    style={iconButton}
                >
                    +
                </button>
            </div>

            {unstamped.length && !repaired ? (
                <div
                    style={{
                        margin: '0 2px 8px',
                        padding: '8px 10px',
                        background: 'var(--amber-bg)',
                        border: '1px solid var(--amber)',
                        borderRadius: 'var(--r-btn)',
                        fontSize: 'var(--fs-xs)',
                        lineHeight: 1.45,
                    }}
                >
                    <div>
                        {t(unstamped.length === 1 ? 'folders.noRole' : 'folders.noRoleMany', {
                            names: unstamped.map((u) => u.mailbox.name).join(', '),
                        })}
                    </div>
                    <button onClick={() => void stampRoles()} disabled={busy} style={{ ...iconButton, width: 'auto', padding: '0 9px', marginTop: 6 }}>
                        {busy ? t('folders.fixing') : t('folders.fixRoles')}
                    </button>
                </div>
            ) : null}

            {error ? (
                <div style={{ margin: '0 2px 8px', padding: '7px 9px', background: 'var(--red-bg)', border: '1px solid var(--red)', borderRadius: 'var(--r-btn)', color: 'var(--red)', fontSize: 'var(--fs-xs)' }}>
                    {error}
                </div>
            ) : null}

            <div
                onDragOver={(e) => {
                    if (dragId) {
                        e.preventDefault();
                        setDropId('__root__');
                    }
                }}
                onDragLeave={() => setDropId((d) => (d === '__root__' ? null : d))}
                onDrop={(e) => {
                    e.preventDefault();
                    setDropId(null);
                    if (dragId) void reparent(dragId, null);
                    setDragId(null);
                }}
                style={{
                    borderRadius: 'var(--r-btn)',
                    outline: dropId === '__root__' ? '1px dashed var(--accent)' : 'none',
                    outlineOffset: 2,
                }}
            >
                {rows.map((node) => {
                    const m = node.mailbox;
                    const on = m.id === selectedId;
                    const role = (m.role as Role | null) ?? roleFromName(m.name);
                    const isSpecial = Boolean(role && byRole[role]?.id === m.id);
                    const hasKids = node.children.length > 0;
                    const isCollapsed = collapsed.has(m.id);

                    return (
                        <div
                            key={m.id}
                            data-testid="folder-row"
                            data-hit
                            data-folder-name={m.name}
                            // Dragging a folder moves it, which is a rename; the server's own folders refuse both.
                            draggable={canManage && !isSpecial}
                            onDragStart={(e) => {
                                e.stopPropagation();
                                setDragId(m.id);
                                e.dataTransfer.effectAllowed = 'move';
                            }}
                            onDragEnd={() => {
                                setDragId(null);
                                setDropId(null);
                            }}
                            onDragOver={(e) => {
                                const email = e.dataTransfer.types.includes('application/x-webmail-message');
                                const movable = dragId && !isDescendant(mailboxes, dragId, m.id);
                                if (!email && !movable) return;
                                e.preventDefault();
                                e.stopPropagation();
                                setDropId(m.id);
                            }}
                            onDragLeave={() => setDropId((d) => (d === m.id ? null : d))}
                            onDrop={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setDropId(null);
                                const emailId = e.dataTransfer.getData('application/x-webmail-message');
                                if (emailId) {
                                    onMoveMessage?.(emailId, m.id);
                                } else if (dragId && !isDescendant(mailboxes, dragId, m.id)) {
                                    void reparent(dragId, m.id);
                                }
                                setDragId(null);
                            }}
                            onContextMenu={(e) => {
                                if (!canManage) return;
                                e.preventDefault();
                                setMenu({ node, x: e.clientX, y: e.clientY });
                            }}
                            onClick={() => onSelect(m.id)}
                            title={label(m)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 7,
                                padding: '6px 8px',
                                paddingLeft: 8 + node.depth * 13,
                                borderRadius: 'var(--r-btn)',
                                cursor: 'pointer',
                                background: dropId === m.id ? 'var(--accent-bg)' : on ? 'var(--accent-bg)' : 'transparent',
                                outline: dropId === m.id ? '1px dashed var(--accent)' : 'none',
                                outlineOffset: -1,
                                /*
                                    An unsubscribed folder is quieter than a subscribed one, and it
                                    used to say so with opacity: 0.55. Opacity is the wrong tool for
                                    it — it multiplies whatever contrast the colour token had, so a
                                    dim token behind it landed at an effective alpha of 0.37 and the
                                    folder's own name became unreadable (measured at 3.5:1, and axe
                                    called it). The quiet now comes from the colour scale, where the
                                    ratios are known.
                                */
                                color: on ? 'var(--text)' : m.isSubscribed ? 'var(--text-dim)' : 'var(--text-mute)',
                                fontSize: 'var(--fs-sm)',
                                fontWeight: on ? 600 : 400,
                            }}
                        >
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (hasKids) toggle(m.id);
                                }}
                                tabIndex={hasKids ? 0 : -1}
                                aria-label={hasKids ? (isCollapsed ? 'Expand' : 'Collapse') : undefined}
                                style={{
                                    width: 12,
                                    height: 12,
                                    flex: '0 0 auto',
                                    padding: 0,
                                    background: 'transparent',
                                    border: 'none',
                                    visibility: hasKids ? 'visible' : 'hidden',
                                    cursor: hasKids ? 'pointer' : 'default',
                                }}
                            >
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--text-mute)" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                                    <path d={isCollapsed ? 'M9 6l6 6-6 6' : 'M6 9l6 6 6-6'} />
                                </svg>
                            </button>

                            <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke={on ? 'var(--accent-strong)' : 'var(--text-mute)'}
                                strokeWidth="1.6"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                style={{ flex: '0 0 auto' }}
                            >
                                <path d={isSpecial && role ? ICONS[role] : ICONS.folder} />
                            </svg>

                            <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {label(m)}
                            </span>

                            {/*
                                What a right-click does, for a device that has no right click.
                                Renaming, deleting and making a subfolder all live in that menu and
                                were simply unreachable on a phone. It is drawn only where it is
                                needed — see [data-touch-only] in global.css — so the desktop tree
                                keeps the shape it was designed with.
                            */}
                            {canManage ? (
                                <button
                                    data-touch-only
                                    data-hit
                                    aria-label={t('folders.actions')}
                                    title={t('folders.actions')}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        const at = e.currentTarget.getBoundingClientRect();
                                        setMenu({ node, x: at.left - 140, y: at.bottom + 4 });
                                    }}
                                    style={{
                                        flex: '0 0 auto',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        background: 'transparent',
                                        border: 'none',
                                        color: 'var(--text-mute)',
                                        fontFamily: 'var(--font-mono)',
                                        fontSize: 'var(--fs-md)',
                                        lineHeight: 1,
                                    }}
                                >
                                    ⋯
                                </button>
                            ) : null}

                            {m.unreadEmails ? (
                                <span
                                    style={{
                                        fontFamily: 'var(--font-mono)',
                                        fontSize: 'var(--fs-2xs)',
                                        fontWeight: 600,
                                        padding: '1px 5px',
                                        borderRadius: 'var(--r-badge)',
                                        background: on ? 'var(--accent)' : 'var(--chip-bg)',
                                        color: on ? 'var(--on-accent)' : 'var(--accent-strong)',
                                    }}
                                >
                                    {num(m.unreadEmails)}
                                </span>
                            ) : (
                                /*
                                    Seçili satırda --text-dim, değilse --text-mute. Okunmamışı olan
                                    klasörün rozeti zaten seçiliyken renk değiştiriyordu; okunmamışı
                                    OLMAYAN klasörün sayısı değiştirmiyordu ve --accent-bg zemininde
                                    koyu temada 4.45:1 kalıyordu (AA 4.5 istiyor) — --text-dim ile
                                    5.69.

                                    Bugüne kadar görünmemesinin sebebi kayda değer: axe koyu temayı
                                    yalnız TELEFON bağlamında tarıyordu ve orada kenar çubuğu bir
                                    çekmece, yani hiç çizilmiyor. Masaüstü genişliğinde koyu temada
                                    klasör ağacını ilk tarayan kontrol bunu ilk koşuşunda buldu.
                                */
                                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', color: on ? 'var(--text-dim)' : 'var(--text-mute)' }}>
                                    {num(m.totalEmails)}
                                </span>
                            )}
                        </div>
                    );
                })}
            </div>

            {hidden.length ? (
                <button
                    onClick={() => {
                        const next = !showHidden;
                        setShowHidden(next);
                        localStorage.setItem(HIDDEN_KEY, next ? '1' : '0');
                    }}
                    className="kicker"
                    data-hit
                    style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 8px 4px', background: 'transparent', border: 'none' }}
                >
                    // {showHidden ? t('folders.hideHidden') : t('folders.showHidden')} ({num(hidden.length)})
                </button>
            ) : null}

            {menu ? (
                <>
                    <div onClick={() => setMenu(null)} onContextMenu={(e) => { e.preventDefault(); setMenu(null); }} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
                    <div
                        style={{
                            position: 'fixed',
                            left: Math.min(menu.x, window.innerWidth - 200),
                            top: Math.min(menu.y, window.innerHeight - 220),
                            zIndex: 41,
                            width: 188,
                            padding: 5,
                            background: 'var(--surface)',
                            border: '1px solid var(--border2)',
                            borderRadius: 'var(--r-btn)',
                            boxShadow: '0 12px 30px rgba(0,0,0,0.4)',
                        }}
                    >
                        <MenuItem label={t('folders.newSub')} onClick={() => { setPrompting({ kind: 'create', parent: menu.node.mailbox, value: '' }); setMenu(null); }} />
                        {menuRole ? null : (
                            <MenuItem label={t('folders.rename')} onClick={() => { setPrompting({ kind: 'rename', mailbox: menu.node.mailbox, value: menu.node.mailbox.name }); setMenu(null); }} />
                        )}
                        {/* The inbox is always shown, so hiding it would do nothing. */}
                        {menuRole === 'inbox' ? null : (
                            <MenuItem
                                label={t(menu.node.mailbox.isSubscribed ? 'folders.hide' : 'folders.show')}
                                onClick={() => {
                                    const m = menu.node.mailbox;
                                    setMenu(null);
                                    if (!client || !accountId) return;
                                    void run(() => client.updateMailbox(accountId, m.id, { isSubscribed: !m.isSubscribed }));
                                }}
                            />
                        )}
                        {menu.node.mailbox.parentId && !menuRole ? (
                            <MenuItem label={t('folders.toTop')} onClick={() => { const id = menu.node.mailbox.id; setMenu(null); void reparent(id, null); }} />
                        ) : null}
                        {/*
                            The server's own folders cannot be deleted or renamed — the server refuses
                            it too — so the menu does not offer it. The Trash's one destructive entry
                            used to be "Sil…", and someone emptying it nearly deleted the folder
                            (2026-10-12). Trash and Junk offer what was meant instead.
                        */}
                        {(menuRole === 'trash' || menuRole === 'junk') && menu.node.mailbox.totalEmails > 0 ? (
                            <>
                                <div style={{ height: 1, background: 'var(--border)', margin: '5px 2px' }} />
                                <MenuItem
                                    label={t('folders.empty')}
                                    danger
                                    testId="folder-empty"
                                    onClick={() => { setEmptying(menu.node.mailbox); setMenu(null); }}
                                />
                            </>
                        ) : null}
                        {menuRole ? null : (
                            <>
                                <div style={{ height: 1, background: 'var(--border)', margin: '5px 2px' }} />
                                <MenuItem
                                    label={t('folders.deleteDots')}
                                    danger
                                    testId="folder-delete"
                                    onClick={() => { setPrompting({ kind: 'delete', mailbox: menu.node.mailbox, value: '' }); setMenu(null); }}
                                />
                            </>
                        )}
                    </div>
                </>
            ) : null}

            {prompting ? (
                <>
                    <div onClick={() => setPrompting(null)} style={{ position: 'fixed', inset: 0, zIndex: 70, background: 'rgba(0,0,0,0.45)' }} />
                    <div
                        style={{
                            position: 'fixed',
                            left: '50%',
                            top: '32%',
                            transform: 'translate(-50%, -50%)',
                            zIndex: 71,
                            width: 'min(400px, calc(100vw - 40px))',
                            padding: 18,
                            background: 'var(--surface)',
                            border: '1px solid var(--border2)',
                            borderRadius: 'var(--r-card)',
                            boxShadow: '0 22px 60px rgba(0,0,0,0.5)',
                            display: 'grid',
                            gap: 12,
                        }}
                    >
                        <div>
                            <div style={{ fontSize: 'var(--fs-lg)', fontWeight: 600 }}>
                                {prompting.kind === 'create'
                                    ? prompting.parent
                                        ? t('folders.newIn', { name: prompting.parent.name })
                                        : t('folders.new')
                                    : prompting.kind === 'rename'
                                      ? t('folders.renaming', { name: prompting.mailbox.name })
                                      : t('folders.deleting', { name: prompting.mailbox.name })}
                            </div>
                            {prompting.kind === 'delete' ? (
                                <div style={{ marginTop: 6, fontSize: 'var(--fs-sm)', color: 'var(--text-dim)', lineHeight: 1.5 }}>
                                    {prompting.mailbox.totalEmails > 0
                                        ? t('folders.deleteFull', {
                                              count: num(prompting.mailbox.totalEmails),
                                              name: prompting.mailbox.name,
                                          })
                                        : t('folders.deleteEmpty')}
                                </div>
                            ) : null}
                        </div>

                        {prompting.kind !== 'delete' || prompting.mailbox.totalEmails > 0 ? (
                            <input
                                autoFocus
                                value={prompting.value}
                                onChange={(e) => setPrompting({ ...prompting, value: e.target.value })}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') submitPrompt();
                                    if (e.key === 'Escape') setPrompting(null);
                                }}
                                placeholder={prompting.kind === 'delete' ? prompting.mailbox.name : t('folders.name')}
                                maxLength={255}
                                spellCheck={false}
                                style={{
                                    height: 32,
                                    padding: '0 10px',
                                    background: 'var(--surface2)',
                                    border: '1px solid var(--border)',
                                    borderRadius: 'var(--r-btn)',
                                    outline: 'none',
                                    fontSize: 'var(--fs-md)',
                                }}
                            />
                        ) : null}

                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                            <button onClick={() => setPrompting(null)} style={{ ...iconButton, width: 'auto', padding: '0 12px', height: 30 }}>
                                {t('app.cancel')}
                            </button>
                            <button
                                onClick={submitPrompt}
                                disabled={busy}
                                style={{
                                    height: 30,
                                    padding: '0 14px',
                                    background: prompting.kind === 'delete' ? 'var(--red)' : 'var(--accent)',
                                    color: prompting.kind === 'delete' ? '#fff' : 'var(--on-accent)',
                                    border: 'none',
                                    borderRadius: 'var(--r-btn)',
                                    fontSize: 'var(--fs-sm)',
                                    fontWeight: 600,
                                }}
                            >
                                {t(prompting.kind === 'create' ? 'app.create' : prompting.kind === 'rename' ? 'app.rename' : 'app.delete')}
                            </button>
                        </div>
                    </div>
                </>
            ) : null}

            {emptying && client ? (
                <EmptyFolderDialog
                    client={client}
                    mailbox={emptying}
                    label={label(emptying)}
                    onClose={() => setEmptying(null)}
                    onEmptied={onChanged}
                />
            ) : null}
        </div>
    );
}

function MenuItem({ label, onClick, danger, testId }: { label: string; onClick: () => void; danger?: boolean; testId?: string }) {
    return (
        <button
            data-testid={testId}
            onClick={onClick}
            style={{
                display: 'block',
                width: '100%',
                padding: '6px 9px',
                textAlign: 'left',
                background: 'transparent',
                border: 'none',
                borderRadius: 7,
                color: danger ? 'var(--red)' : 'var(--text-dim)',
                fontSize: 'var(--fs-sm)',
            }}
        >
            {label}
        </button>
    );
}

const iconButton: React.CSSProperties = {
    width: 22,
    height: 22,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'transparent',
    border: '1px solid var(--border2)',
    borderRadius: 'var(--r-btn)',
    color: 'var(--text-dim)',
    fontSize: 'var(--fs-sm)',
    lineHeight: 1,
};
